import { useQuery } from "@apollo/client";
import { PERSON_BY_ID_QUERY } from "../../../../api/person/queries";
import { defaultPersonDisciplinaerStatusColumns } from "../../../../config/person";
import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
} from "../../../accordion/Accordion";
import { defaultPersonSanktionColumns } from "../../../../config/person";

import AktualStatusCard from "./Disciplinaert/AktuelStatusCard";
import DisciplinaerStatus from "./Disciplinaert/DisciplinaerStatus";
import Sanktioner from "./Disciplinaert/Sanktioner";
import useActiveColumns from "../../../../hooks/useActiveColumns";
import { PersonDto } from "../../../../interfaces/schema";
import usePersonChecks from "../../../../hooks/errorHandling/usePersonChecks";
import { differenceInCalendarDays } from "date-fns";
import { transformQueryDataPerioder } from "../../../../util/apollo";
import { useState } from "react";
import { orderBy } from "../../../../util/orderBy";

export default function Disciplinaert({ personId }: { personId: string }) {
  const [
    personDisciplinaerstatusColumns,
    setPersonDisciplinaerstatusActiveColumns,
  ] = useActiveColumns(
    "person-persondisciplinaerstatus-columns",
    defaultPersonDisciplinaerStatusColumns,
  );

  const [personSanktionColumns, setPersonSanktionActiveColumns] =
    useActiveColumns(
      "person-personsanktion-active-columns",
      defaultPersonSanktionColumns,
    );

  const [data, setData] = useState<
    | {
        person: PersonDto;
      }
    | undefined
  >(undefined);
  const { loading, error, refetch, networkStatus } = useQuery<{
    person: PersonDto;
  }>(
    PERSON_BY_ID_QUERY("personByIdDisciplinaert", {
      personDisciplinaerstatus: true,
      personSanktion: true,
    }),
    {
      variables: { id: personId },
      notifyOnNetworkStatusChange: true,
      onCompleted: (d) => {
        const transformed = transformQueryDataPerioder(d);
        setData(transformed);
      },
    },
  );
  const { checkResult, person } = usePersonChecks(
    loading,
    error,
    data,
    networkStatus,
  );
  if (checkResult) return checkResult;

  const { personSanktion, personDisciplinaerstatus } = person;

  // "Til venstre vil jeg gerne have seneste frakendelse vist, selvom slutdatoen er passeret" #75
  const senesteFrakendelse = orderBy(
    personDisciplinaerstatus,
    "periode.tilDatoInklusiv",
    "desc",
  ).find(
    (ds) =>
      ["Frakendt", "Betinget frakendt", "Midlertidig frakendt"].indexOf(
        ds.disciplinaerstatustypeNrNavigation?.navn ?? "",
      ) > -1,
  );

  // Til højre vil jeg kun have 'Forelægges rådet' vist, hvis slutdatoen ikke er passeret, dvs at det stadig er relevant #75
  const gultKort = orderBy(
    personDisciplinaerstatus,
    "periode.tilDatoInklusiv",
    "desc",
  ).find(
    (ds) =>
      differenceInCalendarDays(
        new Date(ds.periode.tilDatoInklusiv),
        new Date(),
      ) >= 0 &&
      ds.disciplinaerstatustypeNrNavigation?.navn === "Forelægges rådet",
  );
  return (
    <div className="container">
      <div className="row gy-3">
        <div className="col-12 col-lg-8 col-xl-6">
          <AktualStatusCard
            title="Seneste frakendelse"
            status={senesteFrakendelse}
          />
        </div>

        <div className="col-12 col-lg-4 col-xl-6 d-flex align-items-stretch">
          <AktualStatusCard title="Aktuelt gult kort" status={gultKort} />
        </div>
      </div>
      <AccordionRoot localstorageKey="person-id-disciplinaert-accordion-value">
        <AccordionItem
          className="c-card c-card--accordion"
          value="sanktioner"
          isEmpty={!personSanktion?.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Sanktioner</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <Sanktioner
              personId={personId}
              columns={personSanktionColumns}
              setActiveColumns={setPersonSanktionActiveColumns}
              personSanktion={personSanktion ?? []}
              invalidateData={refetch}
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem
          className="c-card c-card--accordion"
          value="discipliaerstatus"
          isEmpty={!personDisciplinaerstatus?.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Disciplinær status</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <DisciplinaerStatus
              personId={personId}
              defaultColumns={defaultPersonDisciplinaerStatusColumns}
              disciplinaerStatus={personDisciplinaerstatus ?? []}
              columns={personDisciplinaerstatusColumns}
              setActiveColumns={setPersonDisciplinaerstatusActiveColumns}
              invalidateData={refetch}
            />
          </AccordionContent>
        </AccordionItem>
      </AccordionRoot>
    </div>
  );
}
